
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableFooter,
    TableRow,
    TablePagination,
    Button,
    Drawer,
    TextField
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {ListViewModel,
        ListViewColumnModel,
        ListViewColumnFactory,
        ListViewFilterModel,
        RecordModel} from '../../models/models';
import {ListViewUIPreferenceModel,
        ListViewUIPreferenceJSON,
        SortOrderModel,
        SortOrderFactory,
        SortDirectionType,
        BarChartUIPreferenceModel} from '../../models/modelsUserConfiguration';
import {PlotlyChartFigure} from "../../models/modelsDashboard";
import {ListConfigurationModel,
        ListViewConfigurationModel} from "../../models/modelsListConfiguration";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {CustomIconComponent} from "../../components/icons/CustomIcon";
import {ListFilterComponent} from "./ListFilter";
import {ListConfigurationComponent} from "./ListConfiguration";
import {ListViewComponent} from "./ListView";
import {BarChartViewComponent} from "./BarChartView";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import FilterListIcon from '@mui/icons-material/FilterList';
import {upsertListView} from "../../services/metadataApi";
import { useAuth } from "../../hooks/useAuth";



export const ListComponent: React.FC<{
  listView: ListViewModel,
  records: RecordModel[],
  listConfig: ListConfigurationModel,
  listViewConfig: ListViewConfigurationModel,
  chart: PlotlyChartFigure | null,
  switchPaginatorPage: (listViewConfig: ListViewConfigurationModel, newPage: number) => void,
  loadSortedRecords: (listViewConfig: ListViewConfigurationModel, sortOrders: SortOrderModel[]) => void,
  loadFilteredRecords: (listViewConfig: ListConfigurationModel) => void,
  onDatabaseSearch: (listViewConfig: ListViewConfigurationModel) => void,
  onFrontendSearch: (listViewConfig: ListViewConfigurationModel) => void,
  setBarChartConfig: (listView: ListViewModel, barChartGroups: BarChartUIPreferenceModel[]) => void,
  setListViewConfig: (listView: ListViewModel, listViewConfig: ListViewConfigurationModel) => void,
  onSave: (listView: ListViewModel) => void,
  selectRecord: (recordUuid: string) => void
}> = ({
  listView,
  records,
  listConfig,
  listViewConfig,
  chart,
  switchPaginatorPage,
  loadSortedRecords,
  loadFilteredRecords,
  setBarChartConfig,
  setListViewConfig,
  onDatabaseSearch,
  onFrontendSearch,
  onSave,
  selectRecord
}) => {

  const columnAlignment = 'left'

  const styles = ContainerClasses();

  const {accessToken, onInvalidToken} = useAuth();
  let {appName, objectName, recordUuid} = useParams();

  const [displayFilters, setDisplayFilters] = useState<boolean>(false);
  const [displaySaveFilters, setDisplaySaveFilters] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const applyFilters = (filters: ListViewFilterModel[], filterLogic: string)  => {

    const errors = filters.map((thisFilter: ListViewFilterModel) => {
      if(thisFilter.Value_sys === null || thisFilter.Value_sys === undefined) return `Error on ${thisFilter.ReferencedField_sys.Label_sys}: empty value not allowed`
      return ''
    }).filter((errorMessage: string) => errorMessage !== '')

    if(errors.length > 0){
      enqueueSnackbar(errors.join('; '), {
        variant: "error",
      });
      return;
    }

    const newListConfig = {...listConfig,
                           applyCustomFilters: true,
                           filterLogic: filterLogic,
                           filters: filters}

    //now, send filters to backend
    loadFilteredRecords(newListConfig)
    setDisplayFilters(false)
  }

  const saveFilters = (overwriteListView: boolean, listViewLabel: string, filters: ListViewFilterModel[], filterLogic: string) => {

    const errors = filters.map((thisFilter: ListViewFilterModel) => {
      if(thisFilter.Value_sys === null || thisFilter.Value_sys === undefined) return `Error on ${thisFilter.ReferencedField_sys.Label_sys}: empty value not allowed`
      return ''
    }).filter((errorMessage: string) => errorMessage !== '')

    if(errors.length > 0){
      enqueueSnackbar(errors.join('; '), {
        variant: "error",
      });
      return;
    }

    const mutableListView = listView.clone()
    mutableListView.Label_sys = listViewLabel
    mutableListView.FilterLogic_sys = filterLogic
    mutableListView.listview_filters = filters

    if(!overwriteListView){
      mutableListView.Id_sys = '';
      mutableListView.Uuid_sys = '';
      mutableListView.ApiName_sys = '';
    }
    const thisObjectName = getObjectName(objectName);
    if(thisObjectName){
      upsertListView(accessToken, thisObjectName, mutableListView)
         .then((thisListView) => {
           //TODO: REDIRECT AND CLOSE
           onSave(thisListView)
         })
         .catch((err) => {
           enqueueSnackbar(err.message, {
             variant: "error",
           });
         })
       }
  }

  const resetFilters = ()  => {
    const newListConfig = {
                            ...listConfig,
                            applyCustomFilters: false,
                            filter_logic: listView.FilterLogic_sys,
                            filters: listView.listview_filters.map((thisFilter: ListViewFilterModel)=>thisFilter)
                           }
    loadFilteredRecords(newListConfig)
    setDisplayFilters(false)
  }


  const getDisplayType = () => {
    if(listView.user_preference?.DisplayType_sys === 'BarChart') return 'BarChart'
    return 'List'
  }

  return (

    <Box>

      <ListFilterComponent
        displayFilters={displayFilters}
        listConfig={listConfig}
        onCloseDialog={() => {setDisplayFilters(false)}}
        onClearDialog={resetFilters}
        onApplyFilters={applyFilters}
        onSaveFilters={saveFilters}
      />

       <Box sx={{flex: 1,
                 display: "flex",
                 flexDirection: "row",
                 justifyContent: "flex-start",
                 alignItems: "center"}}
        >

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2
            }}
          >
            <ListConfigurationComponent
              listView={listView}
              listViewConfig={listViewConfig}
              setBarChartConfig={setBarChartConfig}
              setListViewConfig={setListViewConfig}
            />

            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={() => {setDisplayFilters(!displayFilters)}}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}
              >
                <Typography>
                  Customize Filters
                </Typography>
                <FilterListIcon fontSize="small"/>
              </Box>
            </Button>
          </Box>
        </Box>

        {listView.getDisplayType() === 'List' &&
          <ListViewComponent
            listView={listView}
            records={records}
            switchPaginatorPage={switchPaginatorPage}
            loadSortedRecords={loadSortedRecords}
            onDatabaseSearch={onDatabaseSearch}
            onFrontendSearch={onFrontendSearch}
            onSave = {onSave}
            selectRecord={selectRecord}
            listConfig={listConfig}
            listViewConfig={listViewConfig}
          />
        }
        {listView.getDisplayType() === 'BarChart' &&
         chart !== null &&
          <BarChartViewComponent
            chart={chart}
          />
        }

    </Box>
  );

};
